import React, { useEffect, useMemo, useState } from 'react';
import { Box, ThemeProvider, useTheme } from '@mui/material';
import { I18nextProvider } from 'react-i18next';
import { createHelpTheme, helpTypographySx } from '../../constants/help-typography';
import { helpI18n, isHelpRtl } from '../../i18n/help';

interface HelpI18nProviderProps {
  children: React.ReactNode;
}

const HelpI18nShell: React.FC<{ language: string; children: React.ReactNode }> = ({
  language,
  children,
}) => {
  const parentTheme = useTheme();
  const helpTheme = useMemo(
    () => createHelpTheme(parentTheme, language),
    [parentTheme, language]
  );
  const lang = language.split('-')[0] || 'en';
  const rtl = isHelpRtl(language);

  return (
    <ThemeProvider theme={helpTheme}>
      <Box
        dir={rtl ? 'rtl' : 'ltr'}
        lang={lang}
        sx={{ width: '100%', ...helpTypographySx(language) }}
      >
        {children}
      </Box>
    </ThemeProvider>
  );
};

const HelpI18nProvider: React.FC<HelpI18nProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState(helpI18n.language);

  useEffect(() => {
    const onLanguageChanged = (lng: string) => setLanguage(lng);
    helpI18n.on('languageChanged', onLanguageChanged);
    return () => {
      helpI18n.off('languageChanged', onLanguageChanged);
    };
  }, []);

  return (
    <I18nextProvider i18n={helpI18n}>
      <HelpI18nShell language={language}>{children}</HelpI18nShell>
    </I18nextProvider>
  );
};

export default HelpI18nProvider;
